// Monthly in-force premium for a book of business, summed from enrollments.
//
// One rule for every surface that shows "premium" for a client or agency
// (/renewals, Reports dashboard + CSVs) so they agree (DEV-327).
// client_plans.total_premium is NOT a source: library links are created with
// "0.00" and never updated.
//
// Feed it rows from GET /api/enrollments, whose totalPremium the server
// recomputes from rates. Only base (employee) rows count — that computed
// premium already includes the covered dependents, and dependent rows would
// double-count them.
//
// Two rules:
// - Book (countsTowardBookPremium): what is in force today. Group active or
//   pending (onboarding), active non-waived enrollment, employee not on COBRA,
//   effective date (enrollment and plan) reached, not ended. A pending group
//   with no live enrollment therefore adds nothing.
// - Annual revenue (ytdBilledMonths): calendar year to date, Jan 1 (local)
//   through today. Each row counts its monthly premium once per month it was in
//   force this year, so groups/enrollments that left earlier this year keep
//   their months up to their end date and nothing from prior years counts. A
//   month where coverage starts or ends mid-month is prorated by day.
//   Group status is ignored here (a group that left is usually inactive now).
//
// COBRA is excluded from both: enrollment status "cobra", or the employee's
// status "cobra" (employeeStatus, which GET /api/enrollments adds) even when the
// enrollment row is still "active".
//
// Dates are compared as calendar dates (YYYY-MM-DD) against the viewer's local
// today, so a 2027-03-01 effective date goes live on Mar 1 in every US zone.

export interface BookPremiumEnrollment {
  clientId?: number | null;
  planId?: number | null;
  dependentId?: number | null;
  status?: string | null;
  /** employees.status of the enrolled employee */
  employeeStatus?: string | null;
  waivedCoverage?: boolean | null;
  totalPremium?: string | number | null;
  effectiveDate?: string | Date | null;
  endDate?: string | Date | null;
}

/** Group statuses whose live enrollments are in the book ("pending" = onboarding). */
export const BOOK_CLIENT_STATUSES: readonly string[] = ["active", "pending"];

export interface BookPremiumContext {
  /** Defaults to now. Only its local calendar date is used. */
  today?: Date;
  /** Clients with a BOOK_CLIENT_STATUSES status. When set, the book excludes every other client. */
  bookClientIds?: ReadonlySet<number>;
  /** planId -> plan effective date; a plan counts once that date has arrived. */
  planEffectiveDates?: ReadonlyMap<number, string | Date | null | undefined>;
}

/** Builds the context from the lists the broker pages already load. */
export function buildBookPremiumContext(input: {
  clients?: Array<{ id: number; status?: string | null }> | null;
  plans?: Array<{ id: number; effectiveDate?: string | Date | null }> | null;
  today?: Date;
}): BookPremiumContext {
  return {
    today: input.today,
    bookClientIds: input.clients
      ? new Set(
          input.clients
            .filter(c => BOOK_CLIENT_STATUSES.includes(String(c.status ?? "").toLowerCase()))
            .map(c => Number(c.id)),
        )
      : undefined,
    planEffectiveDates: new Map((input.plans ?? []).map(p => [Number(p.id), p.effectiveDate ?? null])),
  };
}

/** "YYYY-MM-DD" for a stored date (its calendar date, no zone shift) or null. */
function storedYmd(v: unknown): string | null {
  if (v == null || v === "") return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : localYmd(v);
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(v));
  return m ? m[1] : null;
}

function localYmd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const isCobra = (e: BookPremiumEnrollment) =>
  String(e?.status ?? "").toLowerCase() === "cobra" || String(e?.employeeStatus ?? "").toLowerCase() === "cobra";

/** Employee row, not waived, not COBRA, started by `today`. Returns the coverage start (or null if undated). */
function liveStart(e: BookPremiumEnrollment, ctx: BookPremiumContext, today: string): { start: string | null } | null {
  if (e?.dependentId != null) return null;
  if (e?.waivedCoverage === true) return null;
  if (isCobra(e)) return null;
  const eff = storedYmd(e?.effectiveDate);
  if (eff && eff > today) return null;
  const planEff = e?.planId != null ? storedYmd(ctx.planEffectiveDates?.get(Number(e.planId))) : null;
  if (planEff && planEff > today) return null;
  const start = eff && planEff ? (eff > planEff ? eff : planEff) : eff ?? planEff;
  return { start };
}

/** In force today: active/onboarding group, active non-waived non-COBRA employee row, started, not ended. */
export function countsTowardBookPremium(e: BookPremiumEnrollment, ctx: BookPremiumContext = {}): boolean {
  const today = localYmd(ctx.today ?? new Date());
  if (!liveStart(e, ctx, today)) return false;
  if (String(e?.status ?? "").toLowerCase() !== "active") return false;
  const end = storedYmd(e?.endDate);
  if (end && end < today) return false;
  if (ctx.bookClientIds && (e?.clientId == null || !ctx.bookClientIds.has(Number(e.clientId)))) return false;
  return true;
}

/**
 * Months of the current calendar year (1 = Jan … today's month) in which the
 * row was in force: on from its effective date (enrollment and plan) through
 * its end date, or through today when it has none. A month counts when
 * coverage touches any day of it. Rows must be active, or terminated with an
 * end date (without one there's no telling when they left). Waived, COBRA,
 * pending and future-dated rows have none.
 */
export function ytdBilledMonths(e: BookPremiumEnrollment, ctx: BookPremiumContext = {}): number[] {
  return ytdBilledMonthShares(e, ctx).map(s => s.month);
}

/**
 * ytdBilledMonths with the share of each month that was covered: covered days
 * / days in the month, so a mid-month start or end is prorated by day (local
 * date-only arithmetic). Full months are 1. The current month runs to its last
 * day unless coverage ends earlier, i.e. it is billed in full while coverage
 * runs through today.
 */
export function ytdBilledMonthShares(
  e: BookPremiumEnrollment,
  ctx: BookPremiumContext = {},
): Array<{ month: number; share: number }> {
  const now = ctx.today ?? new Date();
  const today = localYmd(now);
  const live = liveStart(e, ctx, today);
  if (!live) return [];
  const status = String(e?.status ?? "").toLowerCase();
  const end = storedYmd(e?.endDate);
  if (status !== "active" && !(status === "terminated" && end)) return [];
  const year = now.getFullYear();
  const out: Array<{ month: number; share: number }> = [];
  for (let m = 1; m <= now.getMonth() + 1; m++) {
    const mm = String(m).padStart(2, "0");
    const daysInMonth = new Date(year, m, 0).getDate();
    const first = `${year}-${mm}-01`;
    const last = `${year}-${mm}-${String(daysInMonth).padStart(2, "0")}`;
    if (live.start && live.start > last) continue;
    if (end && end < first) continue;
    const fromDay = live.start && live.start > first ? Number(live.start.slice(8, 10)) : 1;
    const toDay = end && end < last ? Number(end.slice(8, 10)) : daysInMonth;
    out.push({ month: m, share: (toDay - fromDay + 1) / daysInMonth });
  }
  return out;
}

/** Billed at some point this calendar year (see ytdBilledMonths). */
export function countsTowardAnnualRevenue(e: BookPremiumEnrollment, ctx: BookPremiumContext = {}): boolean {
  return ytdBilledMonths(e, ctx).length > 0;
}

function monthlyPremiumOf(e: BookPremiumEnrollment): number {
  const n = Number(e?.totalPremium ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/** The enrollment rows that make up the book; group them however a report needs. */
export function bookEnrollments<T extends BookPremiumEnrollment>(
  enrollments: T[] | null | undefined,
  ctx: BookPremiumContext = {},
): T[] {
  return (enrollments ?? []).filter(e => countsTowardBookPremium(e, ctx));
}

export function annualRevenueEnrollments<T extends BookPremiumEnrollment>(
  enrollments: T[] | null | undefined,
  ctx: BookPremiumContext = {},
): T[] {
  return (enrollments ?? []).filter(e => countsTowardAnnualRevenue(e, ctx));
}

/**
 * Calendar-YTD revenue per month: index 0 = January … through today's month.
 * A partly covered month counts its prorated share (ytdBilledMonthShares).
 * Unrounded; round to cents only for display. Sums to sumYtdRevenue.
 */
export function ytdRevenueByMonth(
  enrollments: BookPremiumEnrollment[] | null | undefined,
  ctx: BookPremiumContext = {},
): number[] {
  const out = Array.from({ length: (ctx.today ?? new Date()).getMonth() + 1 }, () => 0);
  for (const e of enrollments ?? []) {
    const premium = monthlyPremiumOf(e);
    for (const { month, share } of ytdBilledMonthShares(e, ctx)) out[month - 1] += premium * share;
  }
  return out;
}

/** Premium billed Jan 1 (local) through today. */
export function sumYtdRevenue(
  enrollments: BookPremiumEnrollment[] | null | undefined,
  ctx: BookPremiumContext = {},
): number {
  return ytdRevenueByMonth(enrollments, ctx).reduce((s, n) => s + n, 0);
}

export function sumMonthlyPremium(rows: BookPremiumEnrollment[]): number {
  let total = 0;
  for (const e of rows) total += monthlyPremiumOf(e);
  return total;
}

export function sumBookMonthlyPremium(
  enrollments: BookPremiumEnrollment[] | null | undefined,
  ctx: BookPremiumContext = {},
): number {
  return sumMonthlyPremium(bookEnrollments(enrollments, ctx));
}

/** clientId -> monthly premium over rows already filtered by one of the rules above. */
export function sumMonthlyPremiumByClient(rows: BookPremiumEnrollment[]): Map<number, number> {
  const out = new Map<number, number>();
  for (const e of rows) {
    if (e.clientId == null) continue;
    const cid = Number(e.clientId);
    out.set(cid, (out.get(cid) ?? 0) + monthlyPremiumOf(e));
  }
  return out;
}

export function monthlyPremiumByClient(
  enrollments: BookPremiumEnrollment[] | null | undefined,
  ctx: BookPremiumContext = {},
): Map<number, number> {
  return sumMonthlyPremiumByClient(bookEnrollments(enrollments, ctx));
}

export const clientPlanPremiumKey = (clientId: number, planId: number) => `${clientId}:${planId}`;

/** Keyed by clientPlanPremiumKey(clientId, planId). */
export function monthlyPremiumByClientPlan(
  enrollments: BookPremiumEnrollment[] | null | undefined,
  ctx: BookPremiumContext = {},
): Map<string, number> {
  const out = new Map<string, number>();
  for (const e of bookEnrollments(enrollments, ctx)) {
    if (e.clientId == null || e.planId == null) continue;
    const key = clientPlanPremiumKey(Number(e.clientId), Number(e.planId));
    out.set(key, (out.get(key) ?? 0) + monthlyPremiumOf(e));
  }
  return out;
}
